import { useQuery } from '@tanstack/react-query'

import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_IMPORTED_ACCOUNTS_INDEXING } from '~/constants/refetch-intervals'
import { useActiveOrg } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { IAddress } from '~/types'

export type ImportStatus = 'indexing' | 'failed' | 'completed'

export type ImportedAccount = {
  id: string
  name: string
  address: IAddress
  importCutoffAt: string | null
  importStatus: ImportStatus
  createdAt: string
}

const NO_ACCOUNTS: ImportedAccount[] = []

// Imports only exist behind the accounting flag; the endpoint rejects
// everyone else.
export const useImportedAccounts = () => {
  const { org, featureFlags } = useActiveOrg()
  const { serverGetRequest } = useServerRequests()

  const { data, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.IMPORTED_ACCOUNTS, org?.id],
    queryFn: async () => {
      if (!org?.id) throw new Error('Org ID required')

      const response = await serverGetRequest<{ accounts: ImportedAccount[] }>({
        path: '/api/org/account/import',
        params: { orgId: org.id },
      })
      if (!response) throw new Error('Failed to fetch imported accounts')
      return response.data.accounts
    },
    enabled: !!org?.id && !!featureFlags?.['accounting-v2'],
    // Keep polling while an import is indexing so its status settles on its
    // own.
    refetchInterval: (query) =>
      query.state.data?.some((account) => account.importStatus === 'indexing')
        ? REFETCH_IMPORTED_ACCOUNTS_INDEXING
        : false,
  })

  return { importedAccounts: data ?? NO_ACCOUNTS, isLoading }
}
